/**
 * Sofia view-context declarations.
 *
 * Sofia should always know what the user is looking at — without screenshots,
 * a vision model, or scraping page text. Pages DECLARE what they render via
 * data attributes, and this registry observes those declarations so every chat
 * turn can attach a compact, typed "view envelope" at send time:
 *
 * - **Primary entity** — a detail page (or drawer) stamps ONE
 *   `data-covi-view='{"kind":"run","id":"...","name":"..."}'` JSON attribute on
 *   its root element. When several are mounted (a drawer over a page), the LAST
 *   in document order wins — portaled drawers/modals render later in the DOM.
 * - **Visible rows** — list pages stamp `data-covi-view-list="<kind>"` on the
 *   element wrapping their table/grid; the rows inside carry the transient highlight
 *   `data-covi-highlight-id` anchors (plus optional `data-covi-name` /
 *   `data-covi-status`). An IntersectionObserver owned by this registry tracks
 *   which anchored rows are ACTUALLY on screen right now.
 * - **List meta** — the same list wrapper may declare `data-covi-list-total` /
 *   `-filters` / `-sort` / `-search` / `-showing` so Sofia knows the shape of
 *   the list beyond the visible slice.
 * - **Editor state** — an unsaved builder page (New Report) has NO id to
 *   declare as a primary, yet its editor holds real user state: which runs
 *   are selected, how the report is configured, and what scope it draws from.
 *   Such a page stamps ONE `data-covi-editor='{"kind":"report",...}'` JSON
 *   attribute on its root; the registry captures it like a primary (last in
 *   document order wins) so Sofia can answer run-list, comparison, and
 *   metric-breakdown questions BEFORE the page is saved.
 * - **Focus stack** — the registry remembers the last few primary entities the
 *   user looked at (deduped, capped, timestamped), so "compare it to the run I
 *   had open before" resolves without pasting ids.
 * - **Selection** — at send time, the user's current text selection (bounded)
 *   plus which declared entity it sits inside, so "why did THIS fail?" grounds
 *   on the highlighted thing.
 *
 * The registry is a per-browser-tab, in-memory singleton owned by the Sofia
 * provider layer (started from `CoviMount` via `useCoviViewObserver`, so it
 * survives client-side navigation) and is deliberately thread-INDEPENDENT:
 * "what am I looking at" is a property of the tab, not of any one chat thread.
 * Nothing here is persisted — the envelope is computed fresh per send and never
 * stored with transcripts.
 *
 * Declarations are DOM-only on purpose: `app-shell` may not import `app-covi`
 * (module-structure guard) and page modules importing app-covi hooks would add
 * module-graph edges (and cycles — app-covi already depends on runs/test-set).
 * Pages write literal data attributes; only app-covi reads them. This mirrors
 * how the transient highlight anchors are declared.
 *
 * WIRE CONTRACT — keep this shape lockstep with Sofia's chat API view-context types.
 * The chat POST body's `context` gains:
 *
 *   context.view = {
 *     route:      app-relative path after /{org}, e.g. "/simulated/runs/abc/results/def"
 *     primary:    {kind, id, name?, run_id?, status?}          (omitted if none)
 *     visible:    [{kind, id, name?, status?}]  ≤20             (omitted if [])
 *     list_meta:  {total?, filters?, sort?, search?, showing?}  (omitted if {})
 *     selection:  {text ≤500, kind?, id?}                       (omitted if none)
 *     editor:     unsaved report selection, original counts and completeness
 *                 (see CoviViewEditorState; omitted if none)
 *   }
 *   context.focus_stack = [{kind, id, name?, viewed_s_ago}] ≤5, most recent
 *   first, EXCLUDES the current primary.                        (omitted if [])
 *
 * FE truncation: visible ≤20, name ≤80 chars, selection.text ≤500 chars; empty
 * and null fields are omitted. Kinds are the closed vocabulary below — unknown
 * kinds are dropped (fail closed to the route-only envelope, never a bad send).
 * Editor lists carry at most 50 IDs each, original counts and explicit truncation.
 * IDs longer than 120 characters are dropped, not shortened; compare-by labels cap at 60.
 */

/** Detail pages/drawers declare their primary entity with this JSON attribute. */
export const COVI_VIEW_ATTRIBUTE = "data-covi-view"
/** List pages declare a row container (value = the rows' kind) with this attribute. */
export const COVI_VIEW_LIST_ATTRIBUTE = "data-covi-view-list"
/**
 * Row anchor attribute — the transient highlight anchors double as the visibility
 * substrate. Kept as a literal (not imported from highlight.ts) because it is
 * part of the DOM contract pages write by hand.
 */
export const COVI_ROW_ANCHOR_ATTRIBUTE = "data-covi-highlight-id"
/** Optional row display name, stamped next to the row anchor. */
export const COVI_ROW_NAME_ATTRIBUTE = "data-covi-name"
/** Optional row status, stamped next to the row anchor. */
export const COVI_ROW_STATUS_ATTRIBUTE = "data-covi-status"
/** Optional list-meta attributes, stamped on the `data-covi-view-list` wrapper. */
export const COVI_LIST_TOTAL_ATTRIBUTE = "data-covi-list-total"
export const COVI_LIST_FILTERS_ATTRIBUTE = "data-covi-list-filters"
export const COVI_LIST_SORT_ATTRIBUTE = "data-covi-list-sort"
export const COVI_LIST_SEARCH_ATTRIBUTE = "data-covi-list-search"
export const COVI_LIST_SHOWING_ATTRIBUTE = "data-covi-list-showing"
/**
 * Unsaved editor pages declare their page-level editor state (selected runs,
 * configuration, scope) with this JSON attribute — they have no saved id to
 * declare as a primary. Last declaration in document order wins, like
 * `data-covi-view`.
 */
export const COVI_EDITOR_ATTRIBUTE = "data-covi-editor"

/** Closed kind vocabulary — lockstep with sofia's ProductViewContext kinds. */
export const COVI_VIEW_KINDS = [
  "alert",
  "run",
  "simulated_conversation",
  "uploaded_conversation",
  "simulation",
  "conversation",
  "metric",
  "persona",
  "test_set",
  "report",
  "agent",
  "dashboard",
  "investigation",
  "monitor",
] as const

export type CoviViewKind = (typeof COVI_VIEW_KINDS)[number]

const KIND_SET: ReadonlySet<string> = new Set(COVI_VIEW_KINDS)

/** The entity the current page/drawer is ABOUT. */
export interface CoviViewPrimary {
  kind: CoviViewKind
  id: string
  name?: string
  /** Parent run for run-nested simulation results. */
  run_id?: string
  status?: string
}

/** One row actually on screen inside a declared list container. */
export interface CoviViewVisibleRow {
  kind: CoviViewKind
  id: string
  name?: string
  status?: string
}

/** Shape of the list the user is looking at, beyond the visible slice. */
export interface CoviViewListMeta {
  total?: number
  filters?: string
  sort?: string
  search?: string
  showing?: string
}

/** The user's current text selection, with provenance when determinable. */
export interface CoviViewSelection {
  text: string
  kind?: CoviViewKind
  id?: string
}

/**
 * Page-level editor state for a page that composes an entity the user has NOT
 * saved yet (New Report). There is no id to reference — the value IS the
 * selected runs, the configuration, and the scope — so Sofia can resolve
 * "these runs" / "compare them" without a saved report id.
 */
export interface CoviViewEditorState {
  kind: "report"
  saved: false
  scope: "simulated" | "uploaded"
  selection_scope: "runs" | "conversations"
  selection_complete: boolean
  run_ids: string[]
  run_count: number
  simulation_output_ids: string[]
  simulation_output_count: number
  metric_ids: string[]
  metric_count: number
  compare_by?: string
  secondary_compare_by?: string
  truncated: boolean
}

/** `context.view` — what is on screen right now. `route` is always present. */
export interface CoviViewEnvelope {
  route: string
  primary?: CoviViewPrimary
  visible?: CoviViewVisibleRow[]
  list_meta?: CoviViewListMeta
  selection?: CoviViewSelection
  editor?: CoviViewEditorState
}

/** One recently-viewed primary entity (`context.focus_stack` entry). */
export interface CoviFocusStackEntry {
  kind: CoviViewKind
  id: string
  name?: string
  /** Whole seconds since the user last had this entity on screen. */
  viewed_s_ago: number
}

/** A send-time capture of the registry: `view` + `focus_stack`, or nothing. */
export interface CoviViewSnapshot {
  view: CoviViewEnvelope | null
  focus_stack: CoviFocusStackEntry[]
}

/** Wire caps (lockstep with sofia's server-side truncation bounds). */
export const VISIBLE_ROWS_LIMIT = 20
export const FOCUS_STACK_LIMIT = 5
export const NAME_MAX_CHARS = 80
export const SELECTION_MAX_CHARS = 500
/** Defensive caps beyond the contract — ids/routes/statuses are short in practice. */
export const ID_MAX_CHARS = 120
export const ROUTE_MAX_CHARS = 300
export const STATUS_MAX_CHARS = 60
export const LIST_META_TEXT_MAX_CHARS = 200
/** Lockstep with sofia-agent/product/view_editor.py; covers all 50 selectable runs. */
const EDITOR_IDS_LIMIT = 50
const EDITOR_COMPARE_BY_MAX_CHARS = 60

export function clip(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

export function asNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null
}

/** Trimmed + clipped attribute/JSON string, or null when absent/blank. */
export function asClippedText(value: unknown, max: number): string | null {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? clip(trimmed, max) : null
}

export function asKind(value: unknown): CoviViewKind | null {
  return typeof value === "string" && KIND_SET.has(value)
    ? (value as CoviViewKind)
    : null
}

/**
 * Parse one `data-covi-view` JSON declaration. Fail closed: malformed JSON, an
 * unknown kind, or a missing id yields null (route-only envelope) — a page's
 * bad declaration must never corrupt or block a send.
 */
export function parseCoviViewDeclaration(
  raw: string | null,
): CoviViewPrimary | null {
  if (!raw) {
    return null
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null
  }
  const record = parsed as Record<string, unknown>
  const kind = asKind(record.kind)
  const id = asNonEmptyString(record.id)
  if (!kind || !id) {
    return null
  }
  const primary: CoviViewPrimary = { kind, id: clip(id, ID_MAX_CHARS) }
  const name = asClippedText(record.name, NAME_MAX_CHARS)
  if (name) {
    primary.name = name
  }
  const runId = asClippedText(record.run_id, ID_MAX_CHARS)
  if (runId) {
    primary.run_id = runId
  }
  const status = asClippedText(record.status, STATUS_MAX_CHARS)
  if (status) {
    primary.status = status
  }
  return primary
}

/** IDs stay intact: shortening one changes which resource it references. */
function parseEditorIds(value: unknown, declaredCount: unknown) {
  if (
    !Array.isArray(value) ||
    typeof declaredCount !== "number" ||
    !Number.isSafeInteger(declaredCount) ||
    declaredCount < 0
  ) {
    return null
  }
  const valid = value.filter(
    (item): item is string =>
      typeof item === "string" &&
      item.length > 0 &&
      item.length <= ID_MAX_CHARS &&
      item !== "." &&
      item !== ".." &&
      !/[\s/\\?#%\p{C}]/u.test(item),
  )
  const unique = [...new Set(valid)]
  if (declaredCount < unique.length) {
    return null
  }
  const ids = unique.slice(0, EDITOR_IDS_LIMIT)
  return {
    ids,
    count: declaredCount,
    incomplete: valid.length !== value.length || declaredCount > ids.length,
  }
}

/** Reject older or malformed contracts that cannot describe selection completeness. */
export function parseCoviEditorDeclaration(
  raw: string | null,
): CoviViewEditorState | null {
  if (!raw) {
    return null
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null
  }
  const record = parsed as Record<string, unknown>
  if (
    record.kind !== "report" ||
    record.saved !== false ||
    (record.scope !== "simulated" && record.scope !== "uploaded") ||
    (record.selection_scope !== "runs" &&
      record.selection_scope !== "conversations") ||
    typeof record.selection_complete !== "boolean" ||
    (record.truncated !== undefined && typeof record.truncated !== "boolean") ||
    (record.scope === "uploaded" && record.selection_scope !== "conversations")
  ) {
    return null
  }
  const runs = parseEditorIds(record.run_ids, record.run_count)
  const simulations = parseEditorIds(
    record.simulation_output_ids,
    record.simulation_output_count,
  )
  const metrics = parseEditorIds(record.metric_ids, record.metric_count)
  if (!runs || !simulations || !metrics) {
    return null
  }
  if (record.selection_scope === "runs" && simulations.count > 0) {
    return null
  }
  const editor: CoviViewEditorState = {
    kind: "report",
    saved: false,
    scope: record.scope,
    selection_scope: record.selection_scope,
    selection_complete: record.selection_complete,
    run_ids: runs.ids,
    run_count: runs.count,
    simulation_output_ids: simulations.ids,
    simulation_output_count: simulations.count,
    metric_ids: metrics.ids,
    metric_count: metrics.count,
    truncated:
      record.truncated === true ||
      runs.incomplete ||
      simulations.incomplete ||
      metrics.incomplete,
  }
  for (const key of ["compare_by", "secondary_compare_by"] as const) {
    const value = asClippedText(record[key], EDITOR_COMPARE_BY_MAX_CHARS)
    if (value) {
      editor[key] = value
    }
    if (
      (typeof record[key] === "string" &&
        record[key].trim().length > EDITOR_COMPARE_BY_MAX_CHARS) ||
      (record[key] != null && typeof record[key] !== "string")
    ) {
      editor.truncated = true
    }
  }
  return editor
}

/**
 * The slice of IntersectionObserver the registry uses — injectable so tests
 * (and jsdom, which has no IntersectionObserver) can drive visibility directly.
 */
export interface CoviIntersectionObserverLike {
  observe: (target: Element) => void
  unobserve: (target: Element) => void
  disconnect: () => void
}

export interface CoviIntersectionChange {
  target: Element
  isIntersecting: boolean
}

export interface CreateCoviViewRegistryOptions {
  /**
   * Build the row-visibility observer; return null when unavailable (the
   * registry then degrades to an empty `visible` — never a blocked send).
   */
  createIntersectionObserver?: (
    onChange: (changes: CoviIntersectionChange[]) => void,
  ) => CoviIntersectionObserverLike | null
  /** Clock override for deterministic `viewed_s_ago` in tests. */
  now?: () => number
}

export interface CoviViewRegistry {
  /** Begin observing declarations. Idempotent; no-op outside the browser. */
  start: () => void
  /** Disconnect observers. Session memory (focus stack) survives a restart. */
  stop: () => void
  /**
   * Active org slug, used to strip the `/{org}` prefix off routes. Changing
   * org clears the focus stack — recent-view memory never crosses orgs.
   */
  setOrgSlug: (slug: string | null) => void
  /** Re-read declarations from the DOM now (route changes, tests). */
  rescan: () => void
  /** Send-time capture. Never throws; `{view: null, focus_stack: []}` on SSR/failure. */
  snapshot: () => CoviViewSnapshot
  /** Clear all session memory (tests, sign-out). */
  reset: () => void
}
